import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useMemo } from 'react';
import { Icon, type IconType } from '@/app/components/icons';
import { IconCall } from '@/app/components/icons/communication/icon-call';
import { IconFacebook } from '@/app/components/icons/social-media/icon-facebook';
import { IconLine } from '@/app/components/icons/social-media/icon-line';
import { IconMessenger } from '@/app/components/icons/social-media/icon-messenger';
import { IconCalendarMonth } from '@/app/components/icons/ui-actions/icon-calendar-month';
import { Link } from '@/app/components/navigation/link';
import { groupDealerTels } from '@/app/components/sections/dealer/utils/format';
import { getDealerFacebook, getDealerLine, getDealerMessenger } from '@/app/components/sections/dealer/utils/mapping-dealer';
import { EVENT_NAME } from '@/app/components/sections/dealer/utils/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import type { ContactInfo, DealerTel } from '@/services/contentstack/models/dealer';
const GROUP_NAME = 'components/shared/dealer/dealer-contact';
interface DealerContactProps {
  className?: string;
  data: ContactInfo;
  selectedVehicle: string;
  showOnlyIcon?: boolean;
}
interface ContactItemProps {
  label: string;
  icon?: IconType;
  children: ReactNode;
  isShowOnlyIcon?: boolean;
  dataTest?: string;
}
interface SocialItemProps extends Omit<ContactItemProps, 'children'> {
  data: {
    link: string;
    name: string;
  };
}
interface PhoneItemProps {
  array: DealerTel[];
  showOnlyIcon?: boolean;
  dataTest?: string;
}
const ContactItem = t0 => {
  const $ = _c(16);
  const {
    label,
    icon,
    dataTest,
    children,
    isShowOnlyIcon: t1
  } = t0;
  const isShowOnlyIcon = t1 === undefined ? false : t1;
  const t2 = !isShowOnlyIcon;
  let t3;
  if ($[0] !== isShowOnlyIcon || $[1] !== t2) {
    t3 = cn({
      "flex gap-x-4": isShowOnlyIcon,
      "grid grid-cols-2 gap-x-10": t2
    });
    $[0] = isShowOnlyIcon;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== icon) {
    t4 = !!icon && <Icon icon={icon} className="size-5" />;
    $[3] = icon;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== dataTest || $[6] !== isShowOnlyIcon || $[7] !== label) {
    t5 = !isShowOnlyIcon && <span className="text-body-1-fixed font-noto-bold text-gray-700" {...createDataAttributes({
      test: dataTest
    })}>{label}</span>;
    $[5] = dataTest;
    $[6] = isShowOnlyIcon;
    $[7] = label;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="flex gap-x-2">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children || $[13] !== t3 || $[14] !== t6) {
    t7 = <div className={t3}>{t6}{children}</div>;
    $[12] = children;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
const SocialItem = t0 => {
  const $ = _c(32);
  const {
    data,
    label,
    icon,
    isShowOnlyIcon: t1
  } = t0;
  const isShowOnlyIcon = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== label) {
    t2 = label.toLowerCase();
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const dataTest = `${t2}`;
  const t3 = !isShowOnlyIcon;
  let t4;
  if ($[2] !== isShowOnlyIcon || $[3] !== t3) {
    t4 = cn("", {
      "grid grid-cols-2 items-center gap-x-10": t3,
      "flex items-center gap-x-4": isShowOnlyIcon
    });
    $[2] = isShowOnlyIcon;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== icon) {
    t5 = !!icon && <Icon icon={icon} className="size-5" />;
    $[5] = icon;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== dataTest || $[8] !== isShowOnlyIcon || $[9] !== label) {
    t6 = !isShowOnlyIcon && <span className="text-body-1-fixed font-noto-bold text-gray-700" {...createDataAttributes({
      test: dataTest
    })}>{label}</span>;
    $[7] = dataTest;
    $[8] = isShowOnlyIcon;
    $[9] = label;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex gap-x-2">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = data.name;
  const t9 = data.link;
  const t10 = !isShowOnlyIcon;
  let t11;
  if ($[14] !== isShowOnlyIcon || $[15] !== t10) {
    t11 = cn("truncate text-gray-700", {
      "text-body-1-fixed": t10,
      "text-body-2-fixed": isShowOnlyIcon
    });
    $[14] = isShowOnlyIcon;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = `${dataTest}-value`;
  let t13;
  if ($[17] !== t12) {
    t13 = createDataAttributes({
      test: t12
    });
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== data.link || $[20] !== data.name) {
    t14 = createDataGA({
      click: true,
      eventName: EVENT_NAME,
      group: GROUP_NAME,
      url: data.link,
      text: data.name
    });
    $[19] = data.link;
    $[20] = data.name;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== data.link || $[23] !== data.name || $[24] !== t11 || $[25] !== t13 || $[26] !== t14) {
    t15 = <Link title={t8} theme="light" target="_blank" href={t9} className={t11} {...t13} {...t14}>{data.name}</Link>;
    $[22] = data.link;
    $[23] = data.name;
    $[24] = t11;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15 || $[29] !== t4 || $[30] !== t7) {
    t16 = <div className={t4}>{t7}{t15}</div>;
    $[28] = t15;
    $[29] = t4;
    $[30] = t7;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
};
const PhoneItem = ({
  array = [],
  showOnlyIcon = false,
  dataTest
}: PhoneItemProps) => {
  return groupDealerTels(array).map((group, index) => {
    const extLabel = group.exts.length > 0 ? ` ต่อ ${group.exts.join(', ')}` : '';
    const gaText = `${group.displayBase}${extLabel}`.trim();
    return <Link key={`${group.stripDash}-${index}`} theme="light" href={`tel:${group.stripDash}`} className={cn('where-span:text-inline-wrap text-gray-700', {
      'text-body-1-fixed': !showOnlyIcon,
      'text-body-2-fixed': showOnlyIcon
    })} {...createDataAttributes({
      test: `${dataTest}-value`
    })} {...createDataGA({
      click: true,
      eventName: EVENT_NAME,
      group: GROUP_NAME,
      url: `tel:${group.stripDash}`,
      text: gaText
    })}>
        {group.displayBase}
        {!!extLabel && <span className="pl-1">{extLabel}</span>}
      </Link>;
  });
};
export const DealerContact = t0 => {
  const $ = _c(39);
  const {
    className,
    data,
    selectedVehicle,
    showOnlyIcon: t1
  } = t0;
  const showOnlyIcon = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== data) {
    t2 = getDealerFacebook(data);
    $[0] = data;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const facebookLink = t2[selectedVehicle];
  let t3;
  if ($[2] !== data) {
    t3 = getDealerMessenger(data);
    $[2] = data;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const messengerLink = t3[selectedVehicle];
  let t4;
  if ($[4] !== data) {
    t4 = getDealerLine(data);
    $[4] = data;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const lineLink = t4[selectedVehicle];
  let t5;
  if ($[6] !== className) {
    t5 = cn("flex flex-col space-y-4", className);
    $[6] = className;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = createDataAttributes({
      test: "dealer-contact"
    });
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconCall />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== data?.main_contact || $[11] !== showOnlyIcon) {
    t8 = (data?.main_contact?.length || 0) > 0 ? <div className="flex flex-col"><PhoneItem array={data?.main_contact} showOnlyIcon={showOnlyIcon} dataTest="phone" /></div> : "-";
    $[10] = data?.main_contact;
    $[11] = showOnlyIcon;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== showOnlyIcon || $[14] !== t8) {
    t9 = <ContactItem label={"\u0E42\u0E17\u0E23\u0E28\u0E31\u0E1E\u0E17\u0E4C"} dataTest="phone" icon={t7} isShowOnlyIcon={showOnlyIcon}>{t8}</ContactItem>;
    $[13] = showOnlyIcon;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconCalendarMonth />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== data?.appointment_contact || $[18] !== showOnlyIcon) {
    t11 = (data?.appointment_contact?.length || 0) > 0 ? <div className="flex flex-col"><PhoneItem array={data?.appointment_contact} showOnlyIcon={showOnlyIcon} dataTest="appointment" /></div> : "-";
    $[17] = data?.appointment_contact;
    $[18] = showOnlyIcon;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== showOnlyIcon || $[21] !== t11) {
    t12 = <ContactItem label={"\u0E19\u0E31\u0E14\u0E2B\u0E21\u0E32\u0E22"} dataTest="appointment" icon={t10} isShowOnlyIcon={showOnlyIcon}>{t11}</ContactItem>;
    $[20] = showOnlyIcon;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== facebookLink || $[24] !== showOnlyIcon) {
    t13 = !!facebookLink?.name && <SocialItem data={facebookLink} label="Facebook" icon={<IconFacebook />} isShowOnlyIcon={showOnlyIcon} />;
    $[23] = facebookLink;
    $[24] = showOnlyIcon;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== messengerLink || $[27] !== showOnlyIcon) {
    t14 = !!messengerLink?.name && <SocialItem data={messengerLink} label="Messenger" icon={<IconMessenger />} isShowOnlyIcon={showOnlyIcon} />;
    $[26] = messengerLink;
    $[27] = showOnlyIcon;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== lineLink || $[30] !== showOnlyIcon) {
    t15 = !!lineLink?.name && <SocialItem data={lineLink} label="LINE" icon={<IconLine />} isShowOnlyIcon={showOnlyIcon} />;
    $[29] = lineLink;
    $[30] = showOnlyIcon;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t12 || $[33] !== t13 || $[34] !== t14 || $[35] !== t15 || $[36] !== t5 || $[37] !== t9) {
    t16 = <div className={t5} {...t6}>{t9}{t12}{t13}{t14}{t15}</div>;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t5;
    $[37] = t9;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
};